import { useState } from "react";
import { Images } from "lucide-react";
import { Card, CardContent } from "../components/ui/Card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/Dialog";
import { useGallery } from "../lib/hooks/useGallery";
import {
  buildGalleryLightboxProps,
  buildGalleryThumbnailProps,
} from "../lib/sanity-image-urls";
import type { GalleryPhoto } from "../lib/sanity";

function hasImageAsset(photo: GalleryPhoto): boolean {
  return !!photo.asset;
}

export function Gallery() {
  const { data: gallery, isLoading, isError } = useGallery();
  const [selected, setSelected] = useState<GalleryPhoto | null>(null);

  const categories = (gallery?.categories ?? []).filter((category) =>
    (category.photos ?? []).some(hasImageAsset),
  );

  const lightbox =
    selected !== null ? buildGalleryLightboxProps(selected) : null;
  const lightboxLabel =
    selected?.alt || selected?.caption || "Gallery photo";

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Gallery</h1>
        <p className="text-gray-600 mt-1">
          Photos from club events and sailing weeks
        </p>
      </div>

      {isLoading && (
        <div className="text-center py-12 text-gray-600">Loading gallery…</div>
      )}

      {isError && (
        <Card>
          <CardContent className="py-12 text-center text-gray-600">
            Something went wrong loading the gallery. Please try again later.
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && categories.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-gray-600">
            No gallery photos yet.
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && categories.length > 0 && (
        <div className="space-y-12">
          {categories.map((category) => {
            const photos = (category.photos ?? []).filter(hasImageAsset);
            return (
              <section key={category._key} className="space-y-4">
                <div className="flex items-center gap-2">
                  <Images className="w-5 h-5 shrink-0 text-primary-600" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    {category.title}
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                  {photos.map((photo) => {
                    const thumb = buildGalleryThumbnailProps(photo);
                    const label = photo.alt || photo.caption || "Gallery photo";
                    return (
                      <button
                        key={photo._key}
                        type="button"
                        onClick={() => setSelected(photo)}
                        className="group relative aspect-square overflow-hidden rounded-lg bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
                        aria-label={`View ${label}`}
                      >
                        <img
                          src={thumb.src}
                          srcSet={thumb.srcSet}
                          sizes={thumb.sizes}
                          width={thumb.width}
                          height={thumb.height}
                          alt={label}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                        />
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <Dialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[calc(100vh-5rem)] overflow-y-auto z-50 my-4"
      >
        {selected && lightbox && (
          <>
            <DialogHeader onClose={() => setSelected(null)}>
              <DialogTitle>
                {selected.caption || selected.alt || "Photo"}
              </DialogTitle>
            </DialogHeader>
            <DialogContent>
              <figure className="space-y-3">
                <img
                  src={lightbox.src}
                  srcSet={lightbox.srcSet}
                  sizes={lightbox.sizes}
                  width={lightbox.width}
                  alt={lightboxLabel}
                  className="w-full h-auto rounded-md"
                />
                {selected.caption && (
                  <figcaption className="text-sm text-gray-600 text-center">
                    {selected.caption}
                  </figcaption>
                )}
              </figure>
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
}
